import { Link } from 'react-router-dom'

import type { PackSummary } from '../lib/api'
import { percent, usd } from '../lib/format'
import { MAX_CARDS, MIN_CARDS, hitsFor } from '../lib/packs'
import { Pack3D } from './Pack3D'
import { TIER_ACCENT, TIER_LABEL } from './ui'

/**
 * A shop tile — and with six products rather than twenty-one, a large one.
 *
 * A tile no longer represents a single fixed pack: it represents a tier, and
 * the price it quotes is the floor of a ladder the buyer sets on the pack page.
 * So it quotes a range and the guarantees that hold across the whole range,
 * and sends people to the configurator rather than charging them from here.
 *
 * The art slot is the real 3D wrapper. Every tile on the page shares one WebGL
 * context, so this costs one 2D canvas each rather than one renderer each, and
 * it falls back to the flat artwork without WebGL.
 */
export function PackCard({ pack }: { pack: PackSummary }) {
  const accent = TIER_ACCENT[pack.tier]
  const low = pack.prices[MIN_CARDS] ?? pack.basePrice
  const high = pack.prices[MAX_CARDS] ?? pack.price

  return (
    <article className="panel group relative flex flex-col overflow-hidden">
      {/* Tier colour, kept to a rule and a wash: four packs on a dark page
          need to be told apart at a glance, not shouted at. */}
      <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: accent }} aria-hidden />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(90% 55% at 50% 0%, ${accent}1f 0%, transparent 68%)` }}
        aria-hidden
      />

      <Link
        to={`/pack/${pack.id}`}
        className="relative block transition-transform duration-300 group-hover:-translate-y-1"
        aria-label={`${pack.name}: build and open`}
      >
        <Pack3D pack={pack} className="aspect-[4/4.2] w-full" />
      </Link>

      <div className="relative flex flex-1 flex-col p-5">
        <div className="flex items-baseline justify-between gap-3">
          <Link to={`/pack/${pack.id}`} className="text-xl font-bold tracking-tight hover:underline">
            {TIER_LABEL[pack.tier]}
          </Link>
          <div className="text-right">
            <div className="tnum text-xl font-bold" style={{ color: accent }}>
              {usd(low, { whole: true })}
            </div>
            <div className="eyebrow">to {usd(high, { whole: true })}</div>
          </div>
        </div>

        <dl className="mt-4 space-y-2 text-xs">
          <Row label="Cards" value={`${MIN_CARDS}–${MAX_CARDS}`} />
          <Row label="Guaranteed hits" value={`${hitsFor(MIN_CARDS)}–${hitsFor(MAX_CARDS)}`} />
          <Row label="Published RTP" value={percent(pack.rtp, 1)} accent={accent} />
        </dl>

        <Link to={`/pack/${pack.id}`} className="btn btn-ghost btn-sm mt-5 w-full">
          Build &amp; rip
        </Link>
      </div>
    </article>
  )
}

function Row({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt style={{ color: 'var(--color-faint)' }}>{label}</dt>
      <dd className="tnum font-semibold" style={{ color: accent }}>
        {value}
      </dd>
    </div>
  )
}
